import { Card, Col, Divider, Popover, Row } from 'antd';
import React, { FC, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
	assetInvoiceDetailsDisplayColor,
	getAssetOriginalCost,
	getAssetTotalExpenditure,
} from '../../utils/assetUtils';
import { getCurrency, nullSafeGetOrElse, roundOffToDecimal } from '../../utils/DataAccessUtils';
import { withRouter } from 'react-router';
import { Form } from '@ant-design/compatible';
import { InfoCircleOutlined } from '@ant-design/icons';

const AssetInvoice: FC<any> = ({
	asset,
	userType,
	currentUser,
	card = true,
	inline = false,
}): React.ReactElement => {
	const originalCost = getAssetOriginalCost(asset);
	const currency = useMemo(() => getCurrency({ currentUser }), [currentUser]);

	const invoicesTotal = useMemo(
		() => nullSafeGetOrElse('totalInvoiceSpendOnOpenWrench', asset, 0),
		[asset]
	);
	const totalInternalPartsSpend = useMemo(
		() => nullSafeGetOrElse('totalInternalPartsSpend', asset, 0),
		[asset]
	);
	const totalInternalLaboursSpend = useMemo(
		() => nullSafeGetOrElse('totalInternalLaboursSpend', asset, 0),
		[asset]
	);
	const externalSpend = useMemo(() => nullSafeGetOrElse('externalSpend', asset, 0), [asset]);

	const total = useMemo(() => getAssetTotalExpenditure(asset), [asset]);

	const totalSpent = useMemo(
		() => (
			<div
				className="mt-4 text-center text-4xl font-semibold"
				style={{ color: assetInvoiceDetailsDisplayColor(asset) }}
			>
				{currency.format(total, 0)}
			</div>
		),
		[total, asset]
	);
	const ratio = useMemo(
		() => roundOffToDecimal((total / parseFloat(originalCost)) * 100, 0),
		[total, originalCost]
	);
	const breakdown = (
		<Row className={'justify-center'}>
			<Col>
				<Row className={'justify-end'}>{currency.format(externalSpend)}</Row>
				<Row className={'justify-end'}>{currency.format(invoicesTotal)}</Row>
				<Row className={'justify-end'}>{currency.format(totalInternalPartsSpend)}</Row>
				<Row className={'justify-end'}>{currency.format(totalInternalLaboursSpend)}</Row>
			</Col>
			<Col style={{ marginLeft: 4 }}>
				<Row>spent externally</Row>
				<Row>
					{invoicesTotal > 0 ? (
						<Link
							to={`/${userType}/invoices/overview/all?assetIds=${asset.id}&statuses=Approved%2CProcessing%2CPaid`}
						>
							<div className="text-violet-700">spent on 3rd party invoices</div>
						</Link>
					) : (
						`spent on 3rd party invoices`
					)}
				</Row>
				<Row>spent on parts internally</Row>
				<Row>spent on labor internally</Row>
			</Col>
		</Row>
	);

	if (!card) {
		return (
			<span style={{ color: assetInvoiceDetailsDisplayColor(asset) }}>
				{currency.format(total, 0)}
			</span>
		);
	}

	// Inline mode shows just the line items — used in tooltips/popovers where
	// the total + "spent on maintenance" header would be redundant.
	if (inline) {
		return <div>{breakdown}</div>;
	}

	return (
		<Card>
			{totalSpent}
			<div className="text-center text-base font-normal text-gray-500">
				spent on maintenance
				{originalCost && total ? (
					<Popover
						style={{ marginTop: '-8px' }}
						content={<div>{ratio}% of purchase price spent</div>}
					>
						<span style={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)', marginLeft: 8 }}>
							<InfoCircleOutlined />
						</span>
					</Popover>
				) : null}
			</div>
			<Divider />
			{breakdown}
		</Card>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(AssetInvoice)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(Form.create()(ComponentWithoutUserType));
